import { MessagePanel } from '@dittolive/anvil'
import React from 'react'

import type {
  LiveQueryDestinationKind,
  LiveQueryDialect,
} from '../../../types/dataBridge'
import DataBridgeKeyValue from '../components/DataBridgeKeyValue'
import DataBridgeSectionCard from '../components/DataBridgeSectionCard'
import DataBridgeQueryAccordion from './DataBridgeQueryAccordion'

type Props = {
  /**
   * The collection name (undefined means all collections / firehose)
   */
  collection: string | undefined
  /**
   * The query expression
   */
  expression: string | null | undefined
  /**
   * The query dialect (DQL or OQB/legacy)
   */
  dialect: LiveQueryDialect
  /**
   * Number of partitions (Kafka only)
   */
  partitions?: number | null
  /**
   * The destination kind (kafka or webhook)
   */
  destinationKind: LiveQueryDestinationKind | null
  /**
   * Whether the data bridge uses legacy syntax
   */
  isLegacy?: boolean
  /**
   * Whether the component is in a loading state
   */
  isLoading?: boolean
}

export default function SourceSectionContent({
  collection,
  expression,
  dialect,
  partitions,
  destinationKind,
  isLegacy = false,
  isLoading = false,
}: Props) {
  const isKafkaFirehose = !collection && destinationKind === 'kafka'

  const formatPartitions = (value: unknown) => {
    if (typeof value !== 'number') {
      return '1'
    } else {
      return `${value as number}`
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <DataBridgeSectionCard
        title="Source"
        description="The data that's feeding your bridge"
      >
        {destinationKind === 'kafka' && (
          <DataBridgeKeyValue
            label="Partitions"
            value={partitions}
            isLoading={isLoading}
            formatValue={formatPartitions}
          />
        )}

        <DataBridgeKeyValue
          label="Collection"
          value={collection}
          isLoading={isLoading}
          formatValue={(value: unknown) =>
            !value ? 'All collections' : (value as string)
          }
        />
        <DataBridgeQueryAccordion
          expression={expression}
          dialect={dialect}
          isLoading={isLoading}
        />
      </DataBridgeSectionCard>

      {!isLoading && isLegacy && !isKafkaFirehose && (
        <MessagePanel
          variant="warning"
          message={() => (
            <div>
              <p className="mb-0.5 font-medium">
                This data stream uses legacy syntax
              </p>

              <p>
                You can migrate to the new DQL syntax by recreating the data
                stream and deleting this one.
              </p>
            </div>
          )}
        />
      )}
    </div>
  )
}
